import { AuthLayout } from '@avanta/shared/components/AuthLayout';
import { ADMIN_ROLES } from '@avanta/shared/constants/admins';
import { type AdminRole } from '@avanta/shared/types/enterprises';
import { ALL_USER_TYPES } from '@avanta/shared/types/users';
import { Navigate } from 'react-router';
import { Layout } from 'components/Layout';
import { ROUTES } from 'constants/routes';
import { Administrators } from 'pages/Administrators';
import { AppointmentDetail } from 'pages/AppointmentDetail';
import { Appointments } from 'pages/Appointments';
import { AppointmentsReserve } from 'pages/AppointmentsReserve';
import { AppointmentsSuccess } from 'pages/AppointmentsSuccess';
import { Login } from 'pages/Auth/Login';
import { MagicLink } from 'pages/Auth/MagicLink';
import { RecoverPassword } from 'pages/Auth/RecoverPassword';
import { Billing } from 'pages/Billing';
import { Chat } from 'pages/Chat';
import { Home } from 'pages/Home';
import { Metrics } from 'pages/Metrics';
import { Notifications } from 'pages/Notifications';
import { PatientProfile } from 'pages/PatientProfile';
import { Patients } from 'pages/Patients';
import { Profile } from 'pages/Profile';
import { ProfileSetup } from 'pages/ProfileSetup';

type Path = {
  path: string;
  component: React.ReactElement;
  layout?: React.ReactElement;
  roles?: AdminRole[];
};

const ALL_ROLES = [...Object.values(ADMIN_ROLES)];

export const PRIVATE_PATHS: Path[] = [
  {
    path: ROUTES.HOME,
    component: <Home />,
    layout: <Layout />,
    roles: ALL_ROLES,
  },
  {
    path: ROUTES.PATIENTS,
    component: <Patients />,
    layout: <Layout />,
    roles: [ADMIN_ROLES.ADMIN, ADMIN_ROLES.MANAGER],
  },
  {
    path: ROUTES.APPOINTMENTS,
    component: <Appointments />,
    layout: <Layout />,
    roles: [ADMIN_ROLES.ADMIN, ADMIN_ROLES.MANAGER],
  },
  {
    path: ROUTES.APPOINTMENT_DETAIL,
    component: <AppointmentDetail />,
    layout: <Layout />,
    roles: [ADMIN_ROLES.ADMIN, ADMIN_ROLES.MANAGER],
  },
  {
    path: ROUTES.APPOINTMENTS_RESERVE,
    component: <AppointmentsReserve />,
    roles: [ADMIN_ROLES.ADMIN, ADMIN_ROLES.MANAGER],
  },
  {
    path: ROUTES.APPOINTMENTS_SUCCESS,
    component: <AppointmentsSuccess />,
    roles: [ADMIN_ROLES.ADMIN, ADMIN_ROLES.MANAGER],
  },
  {
    path: ROUTES.PROFILE,
    component: <Profile />,
    layout: <Layout />,
    roles: ALL_ROLES,
  },
  {
    path: ROUTES.PROFILE_SETUP,
    component: <ProfileSetup />,
    roles: ALL_ROLES,
  },

  {
    path: ROUTES.METRICS,
    component: <Metrics />,
    layout: <Layout />,
    roles: [ADMIN_ROLES.ADMIN],
  },

  {
    path: ROUTES.ADMINISTRATORS,
    component: <Administrators />,
    layout: <Layout />,
    roles: [ADMIN_ROLES.ADMIN],
  },
  {
    path: ROUTES.PATIENT_APPOINTMENT,
    component: <AppointmentDetail />,
    layout: <Layout />,
    roles: [ADMIN_ROLES.ADMIN, ADMIN_ROLES.MANAGER],
  },
  {
    path: ROUTES.HOME_APPOINTMENT_DETAIL,
    component: <AppointmentDetail />,
    layout: <Layout />,
    roles: [ADMIN_ROLES.ADMIN, ADMIN_ROLES.MANAGER],
  },
  {
    path: ROUTES.PATIENT_PROFILE,
    component: <PatientProfile />,
    layout: <Layout />,
    roles: [ADMIN_ROLES.ADMIN, ADMIN_ROLES.MANAGER],
  },
  {
    path: ROUTES.BILLING,
    component: <Billing />,
    layout: <Layout />,
    roles: [ADMIN_ROLES.ADMIN, ADMIN_ROLES.BILLING],
  },
  {
    path: ROUTES.DOCS,
    component: <></>,
    layout: <Layout />,
    roles: [ADMIN_ROLES.ADMIN, ADMIN_ROLES.BILLING],
  },
  {
    path: ROUTES.CHAT,
    component: <Chat />,
    layout: <Layout />,
    roles: ALL_ROLES,
  },
  {
    path: ROUTES.NOTIFICATIONS,
    component: <Notifications />,
    layout: <Layout />,
    roles: [ADMIN_ROLES.ADMIN, ADMIN_ROLES.MANAGER],
  },
];

export const PUBLIC_ROUTES: Path[] = [
  {
    path: ROUTES.LOGIN,
    component: <Login />,
    layout: <AuthLayout />,
  },
  {
    path: ROUTES.MAGIC_LINK,
    component: <MagicLink />,
    layout: <AuthLayout />,
  },
  {
    path: ROUTES.RECOVER_PASSWORD,
    component: <RecoverPassword />,
    layout: <AuthLayout />,
  },
];

export const FALLBACK_ROUTE: Path = {
  path: '*',
  component: <Navigate to={ROUTES.HOME} />,
};

export const ROOT = {
  path: ROUTES.ROOT,
  component: <Home />,
  roles: ALL_USER_TYPES,
};
